import { useAuth0 } from "@auth0/auth0-react"
import {
  Avatar,
  Box,
  Divider,
  FormLabel,
  MenuItem,
  Select,
  SelectProps,
  Stack,
  Typography,
} from "@mui/material"
import i18n from "i18next"
import moment from "moment"
import { FC, useEffect } from "react"
import { useTranslation } from "react-i18next"

import { selectProfile } from "src/features/profile/profileSlice"
import { useAppSelector } from "../hooks/hooks"
import LogoutButton from "./LogoutButton"

type ProfileProps = {
  imageWidth?: number
}

export const Profile: FC<ProfileProps> = ({ imageWidth = 150 }) => {
  const { t } = useTranslation()

  const { user, isAuthenticated, isLoading, error, getIdTokenClaims } =
    useAuth0()

  const profile = useAppSelector(selectProfile)

  useEffect(() => {
    const fetchClaims = async () => {
      try {
        const claims = await getIdTokenClaims()
        // setImpersonatingDeliveryUserId(
        //   claims?.["mbls/impersonatingDeliveryUserId"] || null,
        // )
        console.log("Fetched claims:", claims)
      } catch (err) {
        console.error("Error fetching claims:", err)
      }
    }
    fetchClaims()
  }, [getIdTokenClaims])
  if (error) {
    return <Box>{t("errorLoadingUserData")}</Box>
  }

  if (isLoading) {
    return <Box>{t("loading")}</Box>
  }
  if (!profile || !user) {
    return <Box>{t("noUserDataAvailable")}</Box>
  }
  if (!isAuthenticated) {
    return <Box>{t("userNotAuthenticated")}</Box>
  }

  const handleLanguageChange: SelectProps<unknown>["onChange"] = (event) => {
    const newLanguage = event.target.value as string
    localStorage.setItem("language", newLanguage)
    i18n.changeLanguage(newLanguage)
    // for moment, only 'fr' and 'en' are supported; everything else will be 'en'
    moment.locale(newLanguage === "fr" ? "fr" : "en")
  }

  return (
    isAuthenticated && (
      <Stack direction="column" spacing={1}>
        <Stack direction="column" spacing={1} alignItems="center">
          <Avatar
            sx={{
              width: imageWidth,
              height: imageWidth,
              fontSize: imageWidth / 2,
              bgcolor: "primary.main",
            }}
          >
            {profile?.user?.firstName?.[0]?.toUpperCase()}
            {profile?.user?.lastName?.[0]?.toUpperCase()}
          </Avatar>
        </Stack>
        <Typography variant="body1" fontWeight={"bold"}>
          {profile?.user?.firstName} {profile?.user?.lastName}
        </Typography>
        <Typography variant="body1" fontWeight={"bold"}>
          {profile?.user?.username}
        </Typography>

        <Divider />
        <LogoutButton />
        <Box>
          <FormLabel> {t("preferredLang")}</FormLabel>
          <Select
            variant="outlined"
            value={localStorage.getItem("language") || "fr"}
            size="small"
            onChange={handleLanguageChange}
            displayEmpty
            inputProps={{ "aria-label": "Without label" }}
            fullWidth
          >
            <MenuItem value="en">{t("english")}</MenuItem>
            <MenuItem value="fr">{t("french")}</MenuItem>
          </Select>
        </Box>
      </Stack>
    )
  )
}
